import { Store } from "@reduxjs/toolkit";
import {
  GenericOAuth2,
  OAuth2AuthenticateOptions,
} from "@capacitor-community/generic-oauth2";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";

import {
  AuthConfig,
  OAuth2CapacitorSetupOptions,
  IAuthProvider,
} from "../interfaces/IAuthProvider";
import { logout as logoutAction, signIn, updateAuthReady } from "../slice";
import {
  OAUTH2_ID_TOKEN_KEY,
  OAUTH2_REFRESH_TOKEN_KEY,
  OAUTH2_TOKEN_EXPIRE_TIME_KEY,
  OAUTH2_LOGOUT_IN_PROGRESS_KEY,
} from "../constants";

export class OAuth2CapacitorProvider
  implements IAuthProvider<OAuth2CapacitorSetupOptions>
{
  private config: AuthConfig;
  private store: Store | null = null;
  private oauth2token: string | undefined = undefined;
  private appDomain = "com.onmo.virtualconsole";

  constructor(config: AuthConfig) {
    this.config = config;
  }

  private getOAuth2Options(): OAuth2AuthenticateOptions {
    const authorizationUrl = `${this.config.serverUrl}realms/${this.config.realm}/protocol/openid-connect`;
    return {
      appId: this.config.clientId,
      authorizationBaseUrl: `${authorizationUrl}/auth`,
      accessTokenEndpoint: `${authorizationUrl}/token`,
      scope: "openid email profile",
      android: {
        appId: this.config.clientId,
        responseType: "code",
        redirectUrl: `${this.appDomain}://oauth2redirect`,
      },
      ios: {
        appId: this.config.clientId,
        responseType: "code",
        redirectUrl: `${this.appDomain}://oauth2redirect`,
      },
    };
  }

  private composeLogoutUrl(): string {
    const idToken = localStorage.getItem(OAUTH2_ID_TOKEN_KEY);
    const authorizationUrl = `${this.config.serverUrl}realms/${this.config.realm}/protocol/openid-connect/logout`;
    const url = new URL(authorizationUrl);
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set(
      "post_logout_redirect_uri",
      `${this.appDomain}://oauth2redirect`,
    );
    if (idToken) {
      url.searchParams.set("id_token_hint", idToken);
    }
    return url.toString();
  }

  private updateToken(tokenResponse: {
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
    expires_at?: number | string;
  }): void {
    this.oauth2token = tokenResponse?.access_token;
    localStorage.setItem(
      OAUTH2_TOKEN_EXPIRE_TIME_KEY,
      tokenResponse?.expires_at?.toString() || "",
    );
    localStorage.setItem(
      OAUTH2_REFRESH_TOKEN_KEY,
      tokenResponse?.refresh_token || "",
    );
    localStorage.setItem(OAUTH2_ID_TOKEN_KEY, tokenResponse?.id_token || "");
  }

  private async authenticate(): Promise<void> {
    const refreshToken = localStorage.getItem(OAUTH2_REFRESH_TOKEN_KEY);
    const oauth2Options = this.getOAuth2Options();

    if (refreshToken) {
      const refreshTokenResponse = await GenericOAuth2.refreshToken({
        appId: oauth2Options.appId || "",
        accessTokenEndpoint: oauth2Options.accessTokenEndpoint || "",
        refreshToken: refreshToken,
      });
      this.updateToken(refreshTokenResponse);
    } else {
      const authenticateResponse = await GenericOAuth2.authenticate(
        oauth2Options,
      );
      this.updateToken(authenticateResponse?.access_token_response);
    }
  }

  async setup(
    options: OAuth2CapacitorSetupOptions,
  ): Promise<{ authenticated: boolean }> {
    this.store = options.store;
    this.appDomain = options.appDomain || "com.onmo.virtualconsole";
    const dispatch = this.store.dispatch;

    try {
      await this.authenticate();
      dispatch(signIn());
      return { authenticated: true };
    } catch (error) {
      const errorMessage =
        typeof error === "string" ? error : (error as Error).message;
      console.log("Failed to initialize oauth2", errorMessage);
      return { authenticated: false };
    } finally {
      dispatch(updateAuthReady(true));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async login(redirectUri?: string): Promise<void> {
    await this.authenticate();
    if (this.store && this.oauth2token) {
      this.store.dispatch(signIn());
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async logout(ignoreDispatch = false): Promise<void> {
    try {
      // Compose logout URL before clearing tokens (needs id_token)
      const logoutUrl = this.composeLogoutUrl();

      // Clear local tokens
      localStorage.removeItem(OAUTH2_REFRESH_TOKEN_KEY);
      localStorage.removeItem(OAUTH2_ID_TOKEN_KEY);
      localStorage.removeItem(OAUTH2_TOKEN_EXPIRE_TIME_KEY);
      this.oauth2token = undefined;
      this.store?.dispatch(logoutAction());

      if (Capacitor.getPlatform() === "web") {
        window.location.href = logoutUrl;
      } else {
        // Set flag to trigger login when app resumes
        sessionStorage.setItem(OAUTH2_LOGOUT_IN_PROGRESS_KEY, "true");

        // Listen for app resume to trigger login
        const resumeHandler = async () => {
          const logoutInProgress = sessionStorage.getItem(
            OAUTH2_LOGOUT_IN_PROGRESS_KEY,
          );
          if (logoutInProgress) {
            sessionStorage.removeItem(OAUTH2_LOGOUT_IN_PROGRESS_KEY);
            App.removeAllListeners();
            Browser.removeAllListeners();
            // Small delay to ensure browser is fully closed
            setTimeout(() => this.login(), 500);
          }
        };

        await App.addListener("resume", resumeHandler);
        await Browser.addListener("browserFinished", resumeHandler);
        await Browser.open({ url: logoutUrl });
      }
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  async getToken(): Promise<string | undefined> {
    try {
      if (this.oauth2token) {
        const prevTokenExpiresTime =
          parseInt(localStorage.getItem(OAUTH2_TOKEN_EXPIRE_TIME_KEY) || "") ||
          Date.now();
        const currentTime = Date.now();
        if (prevTokenExpiresTime - currentTime < 30000) {
          await this.authenticate();
        }
        return this.oauth2token;
      }
    } catch (error) {
      console.log(error);
    }
    return undefined;
  }

  getCurrentToken(): string | undefined {
    return this.oauth2token;
  }

  getClientId(): string {
    return this.config.clientId;
  }

  isInternalAccount(): boolean {
    return false;
  }
}
